import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import { buildSkinsPlayers, grossToPar, toParLabel } from '../helpers';
import {
  applyMoneyToNetOwed,
  getMoneyAmount,
  getScoringMode,
  getStakeConfig,
} from '../stakes-helpers';
import { strokeMoneySettlement } from '../stake-settlement';

export const strokePlugin: GamePlugin = {
  id: 'stroke',
  label: 'Stroke Play',
  description: 'Lowest gross score wins',
  requiresTeams: false,
  minPlayers: 1,
  defaultStakes: { scoringType: 'none' },
  compute(ctx: RoundContext) {
    const rows = ctx.players.map((p) => ({
      playerKey: p.playerKey,
      name: p.name,
      val: grossToPar(ctx, p.playerKey, false),
    })).filter((r) => r.val != null)
      .sort((a, b) => (a.val as number) - (b.val as number));

    const leader = rows[0];
    const summary = leader
      ? `${leader.name} ${toParLabel(leader.val as number)}`
      : 'Enter scores to see standings';

    return {
      formatId: 'stroke',
      label: 'Stroke Play',
      summary,
      standings: rows.map((r, i) => ({
        playerKey: r.playerKey,
        name: r.name,
        value: toParLabel(r.val as number),
        rank: i + 1,
      })),
      netOwed: applyMoneyToNetOwed(strokeMoneySettlement(ctx, 'stroke', rows, false), getStakeConfig(ctx.stakes, 'stroke')),
      winners: leader ? [leader.playerKey] : [],
    };
  },
};

export const netStrokePlugin: GamePlugin = {
  id: 'net-stroke',
  label: 'Net Stroke',
  description: 'Lowest net score wins',
  requiresTeams: false,
  minPlayers: 1,
  defaultStakes: { scoringType: 'none' },
  compute(ctx: RoundContext) {
    const rows = ctx.players.map((p) => ({
      playerKey: p.playerKey,
      name: p.name,
      val: grossToPar(ctx, p.playerKey, true),
    })).filter((r) => r.val != null)
      .sort((a, b) => (a.val as number) - (b.val as number));

    const leader = rows[0];
    const summary = leader
      ? `${leader.name} ${toParLabel(leader.val as number)} net`
      : 'Enter scores to see standings';

    return {
      formatId: 'net-stroke',
      label: 'Net Stroke',
      summary,
      standings: rows.map((r, i) => ({
        playerKey: r.playerKey,
        name: r.name,
        value: toParLabel(r.val as number),
        rank: i + 1,
      })),
      netOwed: applyMoneyToNetOwed(strokeMoneySettlement(ctx, 'net-stroke', rows, true), getStakeConfig(ctx.stakes, 'net-stroke')),
      winners: leader ? [leader.playerKey] : [],
    };
  },
};

export const matchPlayPlugin: GamePlugin = {
  id: 'match-play',
  label: 'Match Play',
  description: 'Head-to-head match',
  requiresTeams: false,
  isMatch: true,
  minPlayers: 2,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'match-play');
    const me = ctx.players.find((p) => p.you) || ctx.players[0];
    const oppKey = config.opponentKey || ctx.players.find((p) => p.playerKey !== me?.playerKey)?.playerKey;
    const opp = ctx.players.find((p) => p.playerKey === oppKey);
    if (!me || !opp) {
      return { formatId: 'match-play', label: 'Match Play', summary: 'Pick an opponent', netOwed: {} };
    }

    const built = buildSkinsPlayers(ctx, 'match', [me.playerKey, opp.playerKey]);
    const a = built[0];
    const b = built[1];
    const useNet = getScoringMode(stake) !== 'gross';
    const perHole = stake.matchStakeMode === 'per-hole';
    const unit = getMoneyAmount(stake) * 100;

    let meH = 0;
    let oppH = 0;
    const raw: Record<string, number> = { [me.playerKey]: 0, [opp.playerKey]: 0 };

    for (let i = 0; i < 18; i++) {
      const x = useNet ? a.net[i] : a.gross[i];
      const y = useNet ? b.net[i] : b.gross[i];
      if (x == null || y == null) continue;
      if (x < y) {
        meH++;
        if (perHole) {
          raw[me.playerKey] += unit;
          raw[opp.playerKey] -= unit;
        }
      } else if (y < x) {
        oppH++;
        if (perHole) {
          raw[opp.playerKey] += unit;
          raw[me.playerKey] -= unit;
        }
      }
    }
    const diff = meH - oppH;
    if (!perHole) {
      if (diff > 0) {
        raw[me.playerKey] = unit;
        raw[opp.playerKey] = -unit;
      } else if (diff < 0) {
        raw[me.playerKey] = -unit;
        raw[opp.playerKey] = unit;
      }
    }

    const summary = diff === 0
      ? 'All square'
      : diff > 0
        ? `${me.name} ${diff} up`
        : `${opp.name} ${-diff} up`;

    return {
      formatId: 'match-play',
      label: 'Match Play',
      summary: `${me.name} vs ${opp.name} · ${summary}`,
      netOwed: applyMoneyToNetOwed(raw, stake),
      winners: diff > 0 ? [me.playerKey] : diff < 0 ? [opp.playerKey] : [],
      detail: { meH, oppH, diff },
    };
  },
};
